// ============================================================================
// SubordinateTab.tsx — вкладка подчинённого справочника в форме объекта-владельца
// (как в 1С): список записей с отбором owner_id = текущая запись; «Создать»
// создаёт запись с предзаполненным владельцем; клик по строке открывает форму.
// Рендер списка: привязанная КАСТОМНАЯ форма списка (как в 1С), иначе автоформа.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { AutoListForm } from '@/components/autoforms/AutoListForm';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { CustomListFormRenderer } from '@/components/autoforms/CustomListFormRenderer';
import { FileListTab } from '@/components/autoforms/FileListTab';
import { formBindingService } from '@/lib/formBindingService';
import type { ListFilter } from '@/types/formProperties';
import { getSDK } from '@/store/sdkStore';

// Системный справочник «Файлы» (привязка — регистр file_links)
const FILE_CATALOG_CODE = 'files';

interface SubordinateTabProps {
  objectName: string;
  synonym: string;
  ownerId: string;
}

export function SubordinateTab({ objectName, synonym, ownerId }: SubordinateTabProps) {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Привязанная кастомная форма списка (как в 1С: форма списка может быть переопределена)
  const [listBinding, setListBinding] = useState<{ elements: any[]; formAttrs: any[] } | null>(null);
  const [bindingChecked, setBindingChecked] = useState(false);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const handleCreated = useCallback(() => {
    setCreating(false);
    refresh();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const binding = await formBindingService.findBinding('catalog', objectName, 'list');
        if (cancelled) return;
        if (!binding) {
          setBindingChecked(true);
          return;
        }
        let layout: any = {};
        try {
          layout = typeof binding.layout === 'string' ? JSON.parse(binding.layout) : (binding.layout || {});
        } catch { layout = {}; }
        const props = await getSDK().formProperties.getFormProperties({ formId: binding.formId }).catch(() => ({ properties: [] as any[] }));
        const attrs = ((props as any).properties || []).map((p: any) => ({
          name: p.name,
          isMain: !!p.isMain,
          source: p.source as any,
        }));
        if (!cancelled) {
          setListBinding({ elements: (layout.elements || []), formAttrs: attrs });
          setBindingChecked(true);
        }
      } catch {
        if (!cancelled) setBindingChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [objectName]);

  const ownerFilter: ListFilter[] = [{ field: 'owner_id', condition: '=', value: ownerId }];

  // Системный справочник «Файлы»: специализированная вкладка (drag&drop, массовые
  // операции, скрытие вместо удаления)
  if (objectName === FILE_CATALOG_CODE) {
    return (
      <FileListTab objectName={objectName} ownerId={ownerId} />
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {listBinding ? (
        <CustomListFormRenderer
          key={`${objectName}-${refreshKey}`}
          elements={listBinding.elements}
          objectType="catalog"
          objectName={objectName}
          formAttrs={listBinding.formAttrs}
          extraFilters={ownerFilter}
          onRowDoubleClick={(row) => setEditingId(String(row['id'] ?? ''))}
          onCreate={() => setCreating(true)}
        />
      ) : bindingChecked ? (
        <AutoListForm
          key={refreshKey}
          objectType="catalog"
          objectName={objectName}
          filters={ownerFilter}
          onCreate={() => setCreating(true)}
          onSelect={(row) => setEditingId(String(row['id'] ?? ''))}
          showToolbar
        />
      ) : (
        <div className="flex h-16 items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}
      {creating && (
        <AutoObjectForm
          objectType="catalog"
          objectName={objectName}
          initialData={{ owner_id: ownerId }}
          onCreated={handleCreated}
          onClose={() => setCreating(false)}
        />
      )}
      {editingId && (
        <AutoObjectForm
          objectType="catalog"
          objectName={objectName}
          objectId={editingId}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}
